// Fetch functions for canteens
import { fetchDashboardMetricsAction } from '@/features/admin-dashboard/actions/fetchDashboardMetricsAction';
import {
  DashboardMetrics,
  OrderReminderStatusUi,
  SystemAlert,
} from '@/features/admin-dashboard/types';
import { CanteenUi } from '@/features/canteen-management/types';
import { CompanyUI } from '@/features/company-management/types';
import { DietaryRestrictionUI } from '@/features/dietary-restrictions/types';
import { DishUi } from '@/features/dish-management/types';
import { MealOrderDisplay, WeeklyMenuPlan } from '@/features/kitchen-dashboard/types';
import {
  MealRatingDaily,
  MealRatingDailyParams,
  MealRatingStats,
  PendingMealRating,
} from '@/features/meal-rating/types';
import { EmployeeOrderUi, EmployeeUi, InviteUi } from '@/features/user-management/types';
import { MealTime } from '@/lib/enums';
import type { Database } from '@/lib/types';

// Meal translation interface
export interface MealTranslation {
  meal_id: string;
  language_code: 'en' | 'pt';
  name: string;
  description: string | null;
}

export interface TenantUi {
  id: string;
  name: string;
  active: boolean;
  subdomain?: string;
}

export async function fetchCanteens(): Promise<CanteenUi[]> {
  const res = await fetch('/api/canteens');
  if (!res.ok) throw new Error('Failed to fetch canteens');
  return res.json();
}

export async function fetchCanteenById(id: string | number): Promise<CanteenUi | null> {
  const res = await fetch(`/api/canteens/${id}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch canteen');
  return res.json();
}

// Fetch functions for companies
export async function fetchCompanies(): Promise<CompanyUI[]> {
  const res = await fetch('/api/companies');
  if (!res.ok) throw new Error('Failed to fetch companies');
  return res.json();
}

export async function fetchCompanyById(id: string | number): Promise<CompanyUI | null> {
  const res = await fetch(`/api/companies/${id}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch company');
  return res.json();
}

// Fetch functions for dishes
export async function fetchDishes(locale?: string): Promise<DishUi[]> {
  const url = locale ? `/api/dishes?locale=${locale}` : '/api/dishes';
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch dishes');
  return res.json();
}

/**
 * Fetch the soft-deleted catalog (sc-297). Backed by `/api/dishes` with
 * `archived_only=true` so the archived page doesn't have to filter the full
 * catalog response client-side.
 */
export async function fetchArchivedDishes(locale?: string): Promise<DishUi[]> {
  const params = new URLSearchParams({ archived_only: 'true' });
  if (locale) params.set('locale', locale);
  const res = await fetch(`/api/dishes?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch archived dishes');
  return res.json();
}

export async function fetchDishById(id: string | number, locale?: string): Promise<DishUi | null> {
  const url = locale ? `/api/dishes/${id}?locale=${locale}` : `/api/dishes/${id}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch dish');
  return res.json();
}

export async function fetchMealRatingStats(mealId?: string): Promise<MealRatingStats[]> {
  const params = new URLSearchParams();
  if (mealId) params.set('mealId', mealId);

  const res = await fetch(`/api/meal-rating/stats${params.toString() ? `?${params}` : ''}`);
  if (!res.ok) throw new Error('Failed to fetch meal rating stats');
  return res.json();
}

export async function fetchMealRatingDaily(
  params: MealRatingDailyParams,
): Promise<MealRatingDaily[]> {
  const searchParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
  });

  if (params.mealId) searchParams.set('mealId', params.mealId);
  if (params.canteenId) searchParams.set('canteenId', params.canteenId);

  const res = await fetch(`/api/meal-rating/daily?${searchParams}`);
  if (!res.ok) throw new Error('Failed to fetch meal rating analytics');
  return res.json();
}

export async function fetchPendingMealRatings(limit = 10): Promise<PendingMealRating[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  const res = await fetch(`/api/meal-rating/pending?${params}`);
  if (!res.ok) throw new Error('Failed to fetch pending meal ratings');
  return res.json();
}

// Fetch meal translations - separate from dishes
export async function fetchMealTranslations(): Promise<MealTranslation[]> {
  const res = await fetch('/api/meal-translations');
  if (!res.ok) throw new Error('Failed to fetch meal translations');
  return res.json();
}

export async function fetchEmployees(): Promise<EmployeeUi[]> {
  const res = await fetch('/api/employees');
  if (!res.ok) throw new Error('Failed to fetch employees');
  return res.json();
}

export async function fetchOrderReminderStatus(): Promise<OrderReminderStatusUi[]> {
  const res = await fetch('/api/order-reminder-status');
  if (!res.ok) throw new Error('Failed to fetch order reminder status');
  // Dates do not survive JSON, so rehydrate here rather than handing callers a
  // string typed as a Date.
  const rows = (await res.json()) as Array<
    Omit<OrderReminderStatusUi, 'lastRemindedAt'> & { lastRemindedAt: string | null }
  >;
  return rows.map((row) => ({
    ...row,
    lastRemindedAt: row.lastRemindedAt ? new Date(row.lastRemindedAt) : null,
  }));
}

export async function fetchEmployeeById(id: string | number): Promise<EmployeeUi | null> {
  const res = await fetch(`/api/employees/${id}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch employee');
  return res.json();
}

export async function fetchInvites(): Promise<InviteUi[]> {
  const res = await fetch('/api/admin/invites');
  if (!res.ok) throw new Error('Failed to fetch invites');
  return res.json();
}

// Fetch functions for tenants
export async function fetchTenants(): Promise<TenantUi[]> {
  const res = await fetch('/api/tenants');
  if (!res.ok) throw new Error('Failed to fetch tenants');
  return res.json();
}

// Fetch user tenant
export async function fetchUserTenant(): Promise<TenantUi> {
  const res = await fetch('/api/tenants/user');
  if (!res.ok) throw new Error('Failed to fetch user tenant');
  return res.json();
}

// Dietary restrictions fetch functions
export async function fetchDietaryRestrictions(
  language: 'en' | 'pt' = 'en',
): Promise<DietaryRestrictionUI[]> {
  const url = new URL('/api/dietary-restrictions', window.location.origin);
  if (language !== 'en') {
    url.searchParams.set('lang', language);
  }

  const res = await fetch(url.toString());
  if (!res.ok) throw new Error('Failed to fetch dietary restrictions');
  return res.json();
}

export async function fetchDietaryRestrictionById(
  id: string | number,
  language: 'en' | 'pt' = 'en',
): Promise<DietaryRestrictionUI | null> {
  const url = new URL(`/api/dietary-restrictions/${id}`, window.location.origin);
  if (language !== 'en') {
    url.searchParams.set('lang', language);
  }

  const res = await fetch(url.toString());
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch dietary restriction');
  return res.json();
}

// Kitchen Dashboard fetch functions
export async function fetchDailyOrders(
  date: string,
  canteenId: string,
): Promise<MealOrderDisplay[]> {
  const params = new URLSearchParams({
    date,
    canteenId,
  });

  const res = await fetch(`/api/kitchen-dashboard/daily-orders?${params}`);
  if (!res.ok) throw new Error('Failed to fetch daily orders');
  return res.json();
}

export async function fetchWeeklyMenu(
  weekStartDate: string,
  canteenId: string,
  includeDraft: boolean = true, // Default to true for kitchen dashboard
): Promise<WeeklyMenuPlan | null> {
  const params = new URLSearchParams({
    weekStartDate,
    canteenId,
    includeDraft: includeDraft.toString(),
  });

  const res = await fetch(`/api/kitchen-dashboard/weekly-menu?${params}`);
  if (!res.ok) throw new Error('Failed to fetch weekly menu');
  return res.json();
}

// Dashboard metrics fetch function - API Route version (faster)
export async function fetchDashboardMetrics(): Promise<DashboardMetrics> {
  const response = await fetch('/api/dashboard/metrics', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch dashboard metrics');
  }

  const result: DashboardMetrics = await response.json();

  return result;
}

// System alerts fetch functions
export async function fetchSystemAlerts(): Promise<SystemAlert[]> {
  const res = await fetch('/api/admin/dashboard/alerts');
  if (!res.ok) throw new Error('Failed to fetch system alerts');
  const response = await res.json();
  return response.data;
}

export async function fetchSystemAlertById(id: string | number): Promise<SystemAlert | null> {
  // For individual alerts, we can filter from the main alerts endpoint
  // or implement a dedicated endpoint if needed
  const alerts = await fetchSystemAlerts();
  return alerts.find((alert) => alert.id === String(id)) || null;
}

// Activity feed fetch functions
export interface ActivityFeedParams {
  page?: number;
  limit?: number;
  type?: string;
  actor_id?: string;
  target_type?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface ActivityFeedResponse {
  success: boolean;
  data: import('@/features/admin-dashboard/types').ActivityItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
    totalPages: number;
  };
}

export async function fetchActivityFeed(
  params: ActivityFeedParams = {},
): Promise<ActivityFeedResponse> {
  const searchParams = new URLSearchParams();

  // Add parameters to search params
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.type) searchParams.set('type', params.type);
  if (params.actor_id) searchParams.set('actor_id', params.actor_id);
  if (params.target_type) searchParams.set('target_type', params.target_type);
  if (params.date_from) searchParams.set('date_from', params.date_from);
  if (params.date_to) searchParams.set('date_to', params.date_to);
  if (params.search) searchParams.set('search', params.search);

  const url = `/api/admin/dashboard/activity${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const res = await fetch(url);

  if (!res.ok) throw new Error('Failed to fetch activity feed');
  return res.json();
}

export async function fetchActivityFeedById(
  id: string | number,
): Promise<import('@/features/admin-dashboard/types').ActivityItem | null> {
  // For individual activities, we can filter from the main activity feed
  // or implement a dedicated endpoint if needed
  const response = await fetchActivityFeed({ limit: 100 });
  return response.data.find((activity) => activity.id === String(id)) || null;
}

// Weekly menu fetch functions
export async function fetchWeeklyMenus(): Promise<
  import('@/features/weekly-menu-creation/types').WeeklyMenuUI[]
> {
  const res = await fetch('/api/weekly-menus');
  if (!res.ok) throw new Error('Failed to fetch weekly menus');
  return res.json();
}

export async function fetchWeeklyMenuById(
  id: string | number,
): Promise<import('@/features/weekly-menu-creation/types').WeeklyMenuUI | null> {
  const res = await fetch(`/api/weekly-menus/${id}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to fetch weekly menu');
  return res.json();
}

// Distribution KPIs fetch function
export interface DistributionKPIs {
  total_orders: number;
  delivered_count: number;
  unplanned_count: number;
}

export async function fetchDistributionKPIs(
  canteenId: string,
  mealTime: 'lunch' | 'dinner',
  /** Kiosk-local calendar date, YYYY-MM-DD (formatDateOnly). */
  date: string,
): Promise<DistributionKPIs> {
  const params = new URLSearchParams({ canteenId, mealTime, date });

  const res = await fetch(`/api/distribution/kpis?${params}`);
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to fetch distribution KPIs');
  }

  return res.json();
}

export interface AvailableDish {
  meal_id: string;
  meal_name: string;
  course: Database['public']['Enums']['course_type_enum'];
  dish_type: Database['public']['Enums']['dish_type'];
  position: number;
}

export async function fetchAvailableDishes(
  canteenId: string,
  date: string,
  daypart: MealTime,
): Promise<AvailableDish[]> {
  const params = new URLSearchParams({ canteenId, date, daypart });
  const res = await fetch(`/api/distribution/available-dishes?${params}`);
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to fetch available dishes');
  }
  return res.json();
}

// Fetch employee orders for a specific week
export async function fetchEmployeeOrdersForWeek(
  weekStartDate: string,
): Promise<EmployeeOrderUi[]> {
  const params = new URLSearchParams({ weekStartDate });
  const res = await fetch(`/api/employees/orders?${params}`);
  if (!res.ok) throw new Error('Failed to fetch employee orders');
  return res.json();
}

// Notification fetch functions
export interface NotificationPageResponse {
  notifications: import('@/features/notification-center/types').NotificationUI[];
  total: number;
  hasMore: boolean;
}

export async function fetchNotifications(
  limit: number = 20,
  offset: number = 0,
): Promise<NotificationPageResponse> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const res = await fetch(`/api/notifications?${params}`);
  if (!res.ok) throw new Error('Failed to fetch notifications');
  const json = await res.json();
  return {
    ...json,
    notifications: json.notifications.map(
      (n: import('@/features/notification-center/types').NotificationUI) => ({
        ...n,
        createdAt: new Date(n.createdAt),
      }),
    ),
  };
}

export async function fetchUnreadCount(): Promise<number> {
  const res = await fetch('/api/notifications/unread-count');
  if (!res.ok) throw new Error('Failed to fetch unread count');
  const json = await res.json();
  return json.count;
}

// Central registry for query keys, query functions, detail query functions and mutation server actions
